import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Activity, AlertTriangle, Brain, Clock, Gift, Loader2, Mail, MailCheck, RefreshCw,
  Radio, Send, ShieldAlert, TriangleAlert, Users,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import { IncidentWatchAccessManager } from '../IncidentWatchAccess';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const fmt = (iso) => {
  if (!iso) return 'never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 1440) return `${Math.round(mins / 60)} h ago`;
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const STATUS_TONE = {
  clear: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  watch: 'bg-amber-50 text-amber-900 ring-amber-200',
  major: 'bg-red-50 text-red-800 ring-red-200',
};

// Resend's own delivery events, in plain English.
const EVENT_TONE = {
  delivered: ['Delivered', 'bg-emerald-50 text-emerald-700 border-emerald-200'],
  opened: ['Opened', 'bg-emerald-50 text-emerald-700 border-emerald-200'],
  clicked: ['Clicked', 'bg-emerald-50 text-emerald-700 border-emerald-200'],
  sent: ['Accepted', 'bg-slate-50 text-slate-600 border-slate-200'],
  delivery_delayed: ['Delayed', 'bg-amber-50 text-amber-700 border-amber-200'],
  bounced: ['Bounced', 'bg-red-50 text-red-700 border-red-200'],
  complained: ['Marked as spam', 'bg-red-50 text-red-700 border-red-200'],
  failed: ['Failed', 'bg-red-50 text-red-700 border-red-200'],
};

const Stat = ({ label, value, sub, testId, alert }) => (
  <div
    className={`rounded-xl border p-4 ${alert ? 'border-red-200 bg-red-50/50' : 'border-slate-200 bg-white'}`}
    data-testid={testId}
  >
    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{label}</p>
    <p className={`text-3xl font-bold mt-1 tabular-nums ${alert ? 'text-red-700' : 'text-slate-900'}`}>
      {value}
    </p>
    {sub ? <p className="text-xs text-slate-500 mt-0.5">{sub}</p> : null}
  </div>
);

const Section = ({ icon: Icon, title, blurb, children, actions }) => (
  <section className="rounded-xl border border-slate-200 bg-white p-5">
    <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
      <div>
        <h2 className="font-semibold text-slate-900 flex items-center gap-2">
          <Icon size={16} className="text-cyan-600" /> {title}
        </h2>
        {blurb ? <p className="text-xs text-slate-500 mt-1 max-w-xl">{blurb}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
    {children}
  </section>
);

const IncidentWatchAdmin = () => {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [sources, setSources] = useState([]);
  const [ai, setAi] = useState(null);
  const [recipients, setRecipients] = useState(null);
  const [digest, setDigest] = useState(null);
  const [mail, setMail] = useState(null);
  const [domain, setDomain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, src, a, r, d, m] = await Promise.all([
        axios.get(`${API}/api/incident-watch/public/status`),
        axios.get(`${API}/api/admin/incident-watch/sources`, { headers: auth() }),
        axios.get(`${API}/api/admin/incident-watch/ai-usage`, { headers: auth() }),
        axios.get(`${API}/api/admin/incident-watch/alert-recipients`, { headers: auth() }),
        axios.get(`${API}/api/admin/incident-watch/digest-preview`, { headers: auth() }),
        axios.get(`${API}/api/admin/incident-watch/delivery-log`, { headers: auth() }),
      ]);
      setStatus(s.data);
      setSources(src.data.sources || []);
      setAi(a.data);
      setRecipients(r.data);
      setDigest(d.data);
      setMail(m.data);
    } catch (err) {
      toast({
        title: 'Could not load Incident Watch',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (key, url, successTitle, describe) => {
    setBusy(key);
    try {
      const res = await axios.post(`${API}${url}`, {}, { headers: auth() });
      toast({ title: successTitle, description: describe ? describe(res.data) : undefined });
      if (key === 'domain') setDomain(res.data);
      load();
    } catch (err) {
      toast({
        title: 'That did not work',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setBusy(null);
    }
  };

  const degraded = sources.filter((s) => s.status !== 'ok');
  const tone = STATUS_TONE[status?.status_level] || STATUS_TONE.watch;

  if (loading && !status) {
    return (
      <div className="p-10 text-center text-slate-400" data-testid="iw-admin-loading">
        <Loader2 className="animate-spin inline" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-16" data-testid="iw-admin-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Radio size={20} className="text-cyan-600" /> Incident Watch
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Live monitoring, subscriber emails and who has access. Everything here reflects real
            data from the official feeds.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="iw-admin-refresh">
          <RefreshCw size={14} className="mr-2" /> Refresh
        </Button>
      </div>

      <div className={`rounded-xl px-5 py-4 ring-1 ${tone}`} data-testid="iw-admin-status">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] opacity-70">Town status</p>
        <p className="text-xl font-bold mt-0.5">{status?.status_label}</p>
        <p className="text-xs mt-1 opacity-80">
          Last successful check {fmt(status?.last_successful_check)}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Stat label="Live incidents" value={status?.live_count ?? 0}
              sub="genuine, happening now" testId="iw-admin-stat-live"
              alert={(status?.live_count ?? 0) > 0} />
        <Stat label="Planned roadworks" value={status?.roadworks_count ?? 0}
              sub={`${status?.roadworks_closures ?? 0} major closures`} testId="iw-admin-stat-roadworks" />
        <Stat label="Official updates" value={status?.updates_count ?? 0}
              sub="statements, not live" testId="iw-admin-stat-updates" />
        <Stat label="Email recipients" value={recipients?.count ?? 0}
              sub="paying + free access" testId="iw-admin-stat-recipients" />
        <Stat label="AI summaries" value={ai?.summarised ?? 0}
              sub={`${ai?.pending ?? 0} waiting · ${ai?.hourly_remaining ?? 0}/${ai?.hourly_ceiling ?? 0} left this hour`}
              testId="iw-admin-stat-ai" />
      </div>

      {degraded.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-2"
             data-testid="iw-admin-degraded">
          <TriangleAlert size={16} className="text-amber-600 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-900">
            {degraded.length} source{degraded.length > 1 ? 's' : ''} not reporting:{' '}
            <strong>{degraded.map((s) => s.name).join(', ')}</strong>. The public page shows this
            honestly rather than pretending everything is monitored.
          </p>
        </div>
      )}

      <Section
        icon={Activity}
        title="Source health"
        blurb="Where the data comes from. A source that stops reporting is shown to visitors, so nothing silently goes stale."
        actions={
          <Button size="sm" onClick={() => act('run', '/api/admin/incident-watch/run-now', 'Sources checked',
            (d) => `${d.ingested ?? 0} records ingested`)}
            disabled={busy === 'run'} data-testid="iw-admin-run-now">
            {busy === 'run' ? <Loader2 size={14} className="animate-spin mr-2" /> : <RefreshCw size={14} className="mr-2" />}
            Check all sources now
          </Button>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Source</th>
                <th className="text-left px-3 py-2 font-medium">Type</th>
                <th className="text-left px-3 py-2 font-medium">State</th>
                <th className="text-left px-3 py-2 font-medium">Last success</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody data-testid="iw-admin-sources">
              {sources.map((s) => (
                <tr key={s.key} className="border-t border-slate-100">
                  <td className="px-3 py-2.5 font-medium text-slate-900">{s.name}</td>
                  <td className="px-3 py-2.5 text-slate-500">
                    {s.timeliness === 'live' ? 'Live feed' : 'Official statements'}
                  </td>
                  <td className="px-3 py-2.5">
                    {s.status === 'ok' ? (
                      <span className="text-emerald-700">Reporting</span>
                    ) : (
                      <span className="text-amber-700 inline-flex items-center gap-1">
                        <AlertTriangle size={13} /> {s.status}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-slate-600">{fmt(s.last_ok_at)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <Button variant="ghost" size="sm"
                      onClick={() => act(s.key, `/api/admin/incident-watch/run-now?source=${s.key}`,
                        `${s.name} checked`)}
                      disabled={busy === s.key} data-testid={`iw-admin-run-${s.key}`}>
                      {busy === s.key ? <Loader2 size={13} className="animate-spin" /> : 'Check'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <div className="grid gap-6 lg:grid-cols-2">
        <Section
          icon={Brain}
          title="AI summaries"
          blurb="Plain-English summaries are written only for new or materially changed incidents — never for roadworks, and never on a page view. Ceilings protect your budget."
          actions={
            <Button size="sm" variant="outline"
              onClick={() => act('ai', '/api/admin/incident-watch/summarise-now?limit=5', 'Summaries generated',
                (d) => `processed ${d.processed ?? 0}, failed ${d.failed ?? 0}`)}
              disabled={busy === 'ai'} data-testid="iw-admin-summarise">
              {busy === 'ai' ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
              Summarise waiting
            </Button>
          }
        >
          <dl className="text-sm divide-y divide-slate-100">
            {[
              ['Model', ai?.model],
              ['Waiting for a summary', ai?.pending],
              ['Failed', ai?.failed],
              ['Written so far', ai?.summarised],
              ['Used this hour', `${ai?.calls_last_hour ?? 0} of ${ai?.hourly_ceiling ?? 0}`],
              ['Used today', `${ai?.calls_last_day ?? 0} of ${ai?.daily_ceiling ?? 0}`],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between py-2">
                <dt className="text-slate-500">{k}</dt>
                <dd className="font-medium text-slate-900">{String(v ?? '—')}</dd>
              </div>
            ))}
          </dl>
        </Section>

        <Section
          icon={Mail}
          title="Subscriber emails"
          blurb="Major incident alerts go out automatically. The Monday digest is sent once a week. Both go only to people with access — never to free visitors."
        >
          <dl className="text-sm divide-y divide-slate-100 mb-4">
            <div className="flex justify-between py-2">
              <dt className="text-slate-500">Will receive emails</dt>
              <dd className="font-medium text-slate-900">{recipients?.count ?? 0} people</dd>
            </div>
            <div className="flex justify-between py-2">
              <dt className="text-slate-500">In this week's digest</dt>
              <dd className="font-medium text-slate-900">
                {digest?.items?.length ?? 0} closures ({digest?.trunk_count ?? 0} trunk road)
              </dd>
            </div>
          </dl>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline"
              onClick={() => act('alerts', '/api/admin/incident-watch/send-alerts', 'Alert run complete',
                (d) => `sent ${d.sent ?? 0}${d.skipped ? ` (${d.skipped})` : ''}`)}
              disabled={busy === 'alerts'} data-testid="iw-admin-send-alerts">
              {busy === 'alerts' ? <Loader2 size={14} className="animate-spin mr-2" /> : <Send size={14} className="mr-2" />}
              Send any due alerts
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => act('digest', '/api/admin/incident-watch/send-digest?force=true', 'Digest sent',
                (d) => `sent to ${d.sent ?? 0} people`)}
              disabled={busy === 'digest'} data-testid="iw-admin-send-digest">
              {busy === 'digest' ? <Loader2 size={14} className="animate-spin mr-2" /> : <Send size={14} className="mr-2" />}
              Send digest now
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => act('reminders', '/api/admin/incident-watch/send-expiry-reminders', 'Reminders checked',
                (d) => `${d.expiring ?? 0} ending soon, ${d.sent ?? 0} emailed`)}
              disabled={busy === 'reminders'} data-testid="iw-admin-send-reminders">
              {busy === 'reminders' ? <Loader2 size={14} className="animate-spin mr-2" /> : <Clock size={14} className="mr-2" />}
              Check expiring access
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => act('trials', '/api/admin/incident-watch/send-trial-reminders', 'Trials checked',
                (d) => `${d.ending_soon ?? 0} trial(s) ending, ${d.sent ?? 0} reminded`)}
              disabled={busy === 'trials'} data-testid="iw-admin-send-trial-reminders">
              {busy === 'trials' ? <Loader2 size={14} className="animate-spin mr-2" /> : <Clock size={14} className="mr-2" />}
              Check ending trials
            </Button>
          </div>
          {recipients?.recipients?.length ? (
            <p className="text-xs text-slate-500 mt-3" data-testid="iw-admin-recipient-list">
              {recipients.recipients.slice(0, 6).join(', ')}
              {recipients.recipients.length > 6 ? ` and ${recipients.recipients.length - 6} more` : ''}
            </p>
          ) : (
            <p className="text-xs text-slate-500 mt-3">
              Nobody will receive emails yet — grant access or wait for a subscriber.
            </p>
          )}
        </Section>
      </div>

      {digest?.items?.length ? (
        <Section icon={Gift} title="This week's digest"
          blurb="Exactly what paying subscribers and comped accounts will receive on Monday morning, trunk roads first.">
          <ul className="text-sm divide-y divide-slate-100" data-testid="iw-admin-digest-items">
            {digest.items.map((item, i) => (
              <li key={i} className="py-2 flex items-start justify-between gap-4">
                <span className="text-slate-800">
                  {item.trunk ? (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-red-700 mr-2">Trunk</span>
                  ) : null}
                  {item.label}
                </span>
                <span className="text-xs text-slate-500 shrink-0">{item.severity}</span>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      <Section icon={MailCheck} title="Delivery log"
        blurb="What was emailed, and what actually became of it. &quot;Accepted&quot; only means Resend took the message — the status here comes back from Resend after the fact."
        actions={(
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline"
              onClick={() => act('domain', '/api/admin/incident-watch/check-sender-domain', 'Domain checked',
                (d) => d.ok ? 'Verified — I can switch the sender now' : `Still outstanding: ${(d.outstanding || []).join(', ') || d.problem}`)}
              disabled={busy === 'domain'} data-testid="iw-admin-check-domain">
              {busy === 'domain' ? <Loader2 size={14} className="animate-spin mr-2" /> : <ShieldAlert size={14} className="mr-2" />}
              Check DNS now
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => act('replay', '/api/admin/incident-watch/replay-week', 'Replay sent',
                (d) => `${d.count} email(s) re-sent to ${d.to}`)}
              disabled={busy === 'replay'} data-testid="iw-admin-replay-week">
              {busy === 'replay' ? <Loader2 size={14} className="animate-spin mr-2" /> : <RefreshCw size={14} className="mr-2" />}
              Replay last week to me
            </Button>
          </div>
        )}>
        {mail?.sender && !mail.sender.verified ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 mb-4 flex gap-3"
               data-testid="iw-admin-sender-warning">
            <ShieldAlert size={16} className="text-red-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-900">
                Sending as {mail.sender.sender} — this is why alerts go to spam
              </p>
              <p className="text-xs text-red-800 mt-1 leading-relaxed">{mail.sender.problem}</p>
            </div>
          </div>
        ) : null}

        {mail?.untracked ? (
          <p className="text-xs text-slate-500 mb-3">
            {mail.untracked} older email(s) were sent before delivery tracking existed, so only
            &quot;accepted&quot; is known for those.
          </p>
        ) : null}

        {domain?.records?.length ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 mb-4"
               data-testid="iw-admin-dns-checklist">
            <p className="text-sm font-semibold text-slate-900 mb-1">
              {domain.ok ? `${domain.domain} is verified` : `DNS for ${domain.domain}`}
            </p>
            <p className="text-xs text-slate-600 mb-3 leading-relaxed">
              {domain.ok ? domain.next_step : domain.problem}
            </p>
            <div className="space-y-2">
              {domain.records.map((r) => (
                <div key={`${r.type}-${r.name}`}
                     className="rounded border border-slate-200 bg-white p-2 text-xs">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${r.in_dns ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    <span className="font-semibold text-slate-700">{r.type}</span>
                    <span className="text-slate-500">Name:</span>
                    <code className="text-slate-900">{r.name}</code>
                    {r.priority ? <span className="text-slate-500">Priority: {r.priority}</span> : null}
                    <span className={`ml-auto font-medium ${r.in_dns ? 'text-emerald-700' : 'text-red-700'}`}>
                      {r.in_dns ? 'in DNS' : 'not found'}
                    </span>
                  </div>
                  <code className="block break-all text-slate-600 leading-relaxed">{r.value}</code>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {mail?.entries?.length ? (
          <ul className="text-sm divide-y divide-slate-100" data-testid="iw-admin-delivery-log">
            {mail.entries.map((entry) => (
              <li key={`${entry.kind}-${entry.id}`} className="py-2.5">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    {entry.kind}
                  </span>
                  <span className="text-slate-800 font-medium">{entry.label}</span>
                  <span className="text-xs text-slate-500 ml-auto shrink-0">{fmt(entry.created_at)}</span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                  {entry.deliveries.length ? entry.deliveries.map((d, i) => {
                    const [label, tone] = EVENT_TONE[d.last_event] || [d.last_event || 'unknown', 'bg-slate-50 text-slate-600 border-slate-200'];
                    return (
                      <span key={i} className={`text-[11px] px-1.5 py-0.5 rounded border ${tone}`}>
                        {d.email} · {label}
                      </span>
                    );
                  }) : (
                    <span className="text-[11px] text-slate-400">
                      {entry.sent} accepted, no per-message status recorded
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500" data-testid="iw-admin-delivery-empty">
            Nothing emailed in the last fortnight.
          </p>
        )}
      </Section>

      <Section icon={Users} title="Who has access"
        blurb="Paying subscribers appear automatically. You can also give anyone free access for a set period — they get the full feed and the emails.">
        <div className="-mx-5 -mb-5">
          <IncidentWatchAccessManager />
        </div>
      </Section>
    </div>
  );
};

export default IncidentWatchAdmin;
